import { useMemo } from 'react';

import { useAuthStore } from '@/features/auth/stores/auth-store';
import type { Permission } from '@softfactura/shared';

export interface UsePermissionsResult {
  /** Current role string (raw — may be a built-in name or `custom:<id>`). */
  role: string | undefined;
  /** True if the user has the given permission. */
  can: (permission: Permission) => boolean;
  /** True if the user has at least one of the permissions. */
  canAny: (permissions: Permission[]) => boolean;
  /** True if the user has all of the permissions. */
  canAll: (permissions: Permission[]) => boolean;
  /** Full effective permission set for the current user. */
  all: readonly Permission[];
}

/**
 * React hook that reads the current user's effective permission set.
 *
 * The set is computed server-side and shipped on the `/auth/profile` payload
 * (see `apps/api/src/modules/auth/auth.service.ts:getProfile`). This keeps a
 * single source of truth in the backend's PermissionsGuard /
 * permissions-runtime — the frontend simply consumes the resolved list.
 *
 * Prefer `<PermissionGate>` and `<RoleRouteGuard>` for common UI patterns;
 * reach for this hook only when you need the predicate inline (disabled
 * button, branch in a callback, etc.).
 */
export function usePermissions(): UsePermissionsResult {
  const role = useAuthStore((s) => s.user?.role);
  const permissions = useAuthStore((s) => s.user?.permissions);

  return useMemo(() => {
    const granted = permissions ?? [];
    const set = new Set<Permission>(granted);
    return {
      role,
      can: (p) => set.has(p),
      canAny: (perms) => perms.some((p) => set.has(p)),
      canAll: (perms) => perms.every((p) => set.has(p)),
      all: granted,
    };
  }, [role, permissions]);
}
